import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { interviewAPI, resumeAPI } from '../services/api';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ interviews: 0, avgScore: 0, resumes: 0, bestGrade: '-' });
  const [recentInterviews, setRecentInterviews] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [histRes, cvRes] = await Promise.all([
        interviewAPI.history().catch(() => ({ data: { interviews: [] } })),
        resumeAPI.list().catch(() => ({ data: { resumes: [] } })),
      ]);
      const interviews = histRes.data.interviews || [];
      const completed = interviews.filter(i => i.status === 'completed');
      const avgScore = completed.length > 0
        ? (completed.reduce((sum, i) => sum + (parseFloat(i.overall_score) || 0), 0) / completed.length).toFixed(1)
        : 0;
      const bestGrade = completed.length > 0
        ? completed.reduce((best, i) => (!best || (i.grade && i.grade < best) ? i.grade : best), null) || '-'
        : '-';

      setStats({ interviews: interviews.length, avgScore, resumes: (cvRes.data.resumes || []).length, bestGrade });
      setRecentInterviews(interviews.slice(0, 5));
    } catch (err) {
      console.error(err);
    }
  };

  const gradeClass = (g) => {
    if (!g) return 'grade-C';
    if (g.startsWith('A')) return 'grade-A';
    if (g.startsWith('B')) return 'grade-B';
    if (g.startsWith('C')) return 'grade-C';
    return 'grade-D';
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content">
        <div className="page-header">
          <div className="page-title">Welcome back, {user?.name?.split(' ')[0] || 'there'}!</div>
          <div className="page-subtitle">Track your interview preparation progress</div>
        </div>

        <div className="stats-grid">
          <div className="card stat-card">
            <div className="stat-icon purple">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            </div>
            <div><div className="stat-value">{stats.interviews}</div><div className="stat-label">Interviews</div></div>
          </div>
          <div className="card stat-card">
            <div className="stat-icon green">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            </div>
            <div><div className="stat-value">{stats.avgScore}/10</div><div className="stat-label">Avg Score</div></div>
          </div>
          <div className="card stat-card">
            <div className="stat-icon amber">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            </div>
            <div><div className="stat-value">{stats.resumes}</div><div className="stat-label">CVs Uploaded</div></div>
          </div>
          <div className="card stat-card">
            <div className="stat-icon red">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            </div>
            <div><div className="stat-value">{stats.bestGrade}</div><div className="stat-label">Best Grade</div></div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate('/upload-cv')}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>📄</div>
            <div className="card-title">Upload Your CV</div>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 8 }}>
              Upload your resume to get personalized interview questions based on your skills and experience.
            </p>
            <button className="btn btn-primary" style={{ marginTop: 16 }}>Upload CV</button>
          </div>

          <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate('/interview/setup')}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🎯</div>
            <div className="card-title">Start Mock Interview</div>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 8 }}>
              Practice with AI-powered interviews. Choose text chat, MCQ, or voice mode.
            </p>
            <button className="btn btn-primary" style={{ marginTop: 16 }}>Start Interview</button>
          </div>
        </div>

        {recentInterviews.length > 0 && (
          <div className="card" style={{ marginTop: 24 }}>
            <div className="card-header">
              <div className="card-title">Recent Interviews</div>
              <button className="btn btn-sm btn-secondary" onClick={() => navigate('/history')}>View All</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 0', color: 'var(--text-secondary)', fontWeight: 500 }}>Role</th>
                  <th style={{ textAlign: 'left', padding: '8px 0', color: 'var(--text-secondary)', fontWeight: 500 }}>Mode</th>
                  <th style={{ textAlign: 'left', padding: '8px 0', color: 'var(--text-secondary)', fontWeight: 500 }}>Score</th>
                  <th style={{ textAlign: 'left', padding: '8px 0', color: 'var(--text-secondary)', fontWeight: 500 }}>Grade</th>
                  <th style={{ textAlign: 'left', padding: '8px 0', color: 'var(--text-secondary)', fontWeight: 500 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentInterviews.map(iv => (
                  <tr key={iv.id} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onClick={() => iv.status === 'completed' ? navigate(`/interview/${iv.id}/report`) : null}>
                    <td style={{ padding: '12px 0' }}>{iv.job_role || 'General'}</td>
                    <td><span className="tag tag-primary">{iv.mode?.replace('_', ' ')}</span></td>
                    <td>{iv.overall_score ? `${iv.overall_score}/10` : '-'}</td>
                    <td>{iv.grade ? <span className={`grade-badge ${gradeClass(iv.grade)}`} style={{ width: 32, height: 32, fontSize: 14 }}>{iv.grade}</span> : '-'}</td>
                    <td><span className={`tag ${iv.status === 'completed' ? 'tag-success' : 'tag-amber'}`}>{iv.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
